(() => {
  if (window.reviewEditorialInitialized) return;
  window.reviewEditorialInitialized = true;
  // Keep review field captions as plain text without activating their controls.
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const label = target?.closest('.jdgm-form label');
    if (!label || target.closest('a, button, input, textarea, select, [role="button"], .animated-select')) return;
    event.preventDefault();
    event.stopPropagation();
  }, true);
  let scheduled = false;
  const titleEditors = new WeakMap();
  const titleSources = new WeakMap();
  const sizes = new WeakMap();
  const bodyHandles = new WeakMap();
  let resizingBody = null;
  let measureBox = null;
  const configuredValidators = new WeakSet();
  const submittingForms = new WeakSet();
  const fieldValues = new WeakMap();
  const fieldValue = (field) => /^(checkbox|radio)$/.test(field.type) ? `${field.checked}:${field.value}` : field.value;
  const beginSubmission = (form) => {
    form.setAttribute('data-review-submit-attempted', '');
    form.querySelectorAll('input, textarea, select').forEach((field) => fieldValues.set(field, fieldValue(field)));
    submittingForms.add(form);
    window.setTimeout(() => submittingForms.delete(form), 0);
  };
  const configureValidation = (form) => {
    const validator = window.jdgm?.$?.(form).data('validator');
    if (!validator || configuredValidators.has(validator)) return;
    ['element', 'form'].forEach((method) => {
      const validate = validator[method];
      validator[method] = function (...args) {
        if (!submittingForms.has(form)) {
          const fields = method === 'element' ? window.jdgm.$(args[0]).toArray() : form.querySelectorAll('input, textarea, select');
          fields.forEach((field) => clearEditedError({ target: field }));
          return true;
        }
        return validate.apply(this, args);
      };
    });
    configuredValidators.add(validator);
  };
  const clearEditedError = (event) => {
    const field = titleSources.get(event.target) || event.target;
    const form = field.closest?.('.jdgm-form');
    if (!form || !field.matches('input, textarea, select') || /^(submit|button|reset)$/.test(field.type)) return;
    const value = fieldValue(field);
    if (fieldValues.has(field) && fieldValues.get(field) === value) return;
    fieldValues.set(field, value);
    const validator = window.jdgm?.$?.(form).data('validator');
    if (!validator) return;
    validator.errorsFor(field).hide();
    delete validator.invalid[field.name];
    delete validator.submitted[field.name];
    delete validator.errorMap[field.name];
    validator.errorList = validator.errorList.filter((error) => error.element !== field);
    validator.settings.unhighlight?.call(validator, field, validator.settings.errorClass, validator.settings.validClass);
    field.setAttribute('aria-invalid', 'false');
    titleEditors.get(field)?.setAttribute('aria-invalid', 'false');
  };
  const fieldSelector = '.jdgm-form :is(input[name="review_title"], textarea[name="review_body"], textarea[data-review-title-editor])';
  const measureContent = (field) => {
    if (!measureBox) {
      measureBox = document.createElement('textarea');
      measureBox.tabIndex = -1;
      measureBox.setAttribute('aria-hidden', 'true');
      measureBox.style.cssText = 'position:fixed;left:-10000px;top:0;visibility:hidden;pointer-events:none;height:0;min-height:0;max-height:none;overflow:hidden;resize:none;';
      document.body.append(measureBox);
    }
    const style = getComputedStyle(field);
    ['font', 'letterSpacing', 'wordSpacing', 'textIndent', 'textTransform', 'lineHeight', 'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight', 'borderTopWidth', 'borderBottomWidth', 'borderLeftWidth', 'borderRightWidth', 'borderStyle', 'boxSizing', 'whiteSpace', 'overflowWrap', 'wordBreak', 'tabSize', 'direction'].forEach((property) => {
      measureBox.style[property] = style[property];
    });
    measureBox.style.width = `${field.getBoundingClientRect().width}px`;
    measureBox.value = field.value;
    return measureBox.scrollHeight + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
  };
  const resizeField = (field) => {
    if (!field.matches('textarea') || !field.getClientRects().length || !field.clientWidth) return;
    const state = sizes.get(field) || {};
    if (state.dragging) return;
    const title = field.hasAttribute('data-review-title-editor');
    const baseMin = title ? 44 : 136;
    const max = Math.max(baseMin, Math.min(title ? 160 : 480, window.innerHeight * .6));
    const width = field.getBoundingClientRect().width;
    const font = getComputedStyle(field).font;
    if (state.value !== field.value || state.width !== width || state.max !== max || state.font !== font) {
      state.contentHeight = measureContent(field);
      state.value = field.value;
      state.max = max;
      state.font = font;
    }
    const min = Math.max(baseMin, Math.min(max, state.contentHeight));
    if (!title) field.style.minHeight = `${min}px`;
    const scroll = field.scrollTop;
    const height = Math.max(min, Math.min(max, state.manualHeight ?? min));
    field.style.height = `${height}px`;
    field.style.overflowY = !title || field.scrollHeight > field.clientHeight ? 'auto' : 'hidden';
    field.scrollTop = scroll;
    state.height = field.getBoundingClientRect().height;
    state.width = width;
    sizes.set(field, state);
  };
  const sizeObserver = new ResizeObserver((entries) => {
    entries.forEach(({ target }) => {
      positionBodyHandle(target);
      const state = sizes.get(target) || {};
      if (!target.clientWidth) return;
      if (!state.height || Math.abs(state.width - target.getBoundingClientRect().width) > 1) {
        resizeField(target);
        return;
      }
      const height = target.getBoundingClientRect().height;
      if (Math.abs(height - state.height) > 1 && target.name === 'review_body') {
        state.manualHeight = height;
        state.height = height;
        target.style.overflowY = 'auto';
      }
    });
  });
  const positionBodyHandle = (field) => {
    const handle = bodyHandles.get(field);
    if (!handle || !field.getClientRects().length) return;
    const rect = field.getBoundingClientRect();
    const parent = handle.offsetParent;
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();
    handle.style.left = `${rect.right - parentRect.left - parent.clientLeft - handle.offsetWidth}px`;
    handle.style.top = `${rect.bottom - parentRect.top - parent.clientTop - handle.offsetHeight}px`;
  };
  const enhanceBodyResize = (field) => {
    if (bodyHandles.has(field)) return;
    const handle = document.createElement('button');
    handle.type = 'button';
    handle.className = 'review-editorial__resize';
    handle.setAttribute('aria-label', 'Resize review content');
    handle.setAttribute('role', 'separator');
    handle.setAttribute('aria-orientation', 'horizontal');
    if (field.id) handle.setAttribute('aria-controls', field.id);
    field.parentElement.classList.add('review-editorial__body-field');
    field.after(handle);
    bodyHandles.set(field, handle);
    const resize = (height) => {
      const state = sizes.get(field) || {};
      state.manualHeight = height;
      state.dragging = false;
      sizes.set(field, state);
      resizeField(field);
      positionBodyHandle(field);
      const min = parseFloat(getComputedStyle(field).minHeight) || 136;
      const max = Math.max(136, Math.min(480, window.innerHeight * .6));
      handle.setAttribute('aria-valuemin', Math.round(min));
      handle.setAttribute('aria-valuemax', Math.round(max));
      handle.setAttribute('aria-valuenow', Math.round(field.getBoundingClientRect().height));
    };
    let drag;
    handle.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      drag = { id: event.pointerId, y: event.clientY, height: field.getBoundingClientRect().height };
      handle.setPointerCapture(event.pointerId);
    });
    handle.addEventListener('pointermove', (event) => {
      if (drag?.id === event.pointerId) resize(drag.height + event.clientY - drag.y);
    });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((type) => handle.addEventListener(type, () => { drag = null; }));
    handle.addEventListener('keydown', (event) => {
      if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      resize(event.key === 'Home' ? 0 : event.key === 'End' ? Infinity :
        field.getBoundingClientRect().height + (event.key === 'ArrowDown' ? 16 : -16));
    });
    resize(field.getBoundingClientRect().height || 136);
  };
  const limitValue = (value, max) => {
    let result = value.slice(0, max);
    if (/[\uD800-\uDBFF]$/.test(result)) result = result.slice(0, -1);
    return result;
  };
  const enhanceTitle = (source) => {
    if (titleEditors.has(source)) return;
    const editor = document.createElement('textarea');
    editor.className = source.className;
    editor.id = `${source.id || 'ReviewTitle'}-multiline`;
    editor.rows = 1;
    editor.maxLength = 100;
    editor.setAttribute('data-review-title-editor', '');
    editor.placeholder = source.placeholder;
    editor.defaultValue = source.defaultValue;
    editor.value = limitValue(source.value, 100);
    editor.setAttribute('aria-label', source.getAttribute('aria-label') || 'Review Title');
    if (source.hasAttribute('aria-describedby')) editor.setAttribute('aria-describedby', source.getAttribute('aria-describedby'));
    [...source.labels].forEach((label) => { label.htmlFor = editor.id; });
    titleEditors.set(source, editor);
    titleSources.set(editor, source);
    source.maxLength = 100;
    source.classList.add('review-editorial__title-source');
    source.tabIndex = -1;
    source.setAttribute('aria-hidden', 'true');
    // Retain the app's input and its handlers: Judge.me reads and fills it by tag/name.
    const value = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    Object.defineProperty(source, 'value', {
      configurable: true,
      get() { return value.get.call(this); },
      set(next) {
        const text = limitValue(String(next).replace(/[\r\n]+/g, ' '), 100);
        value.set.call(this, text);
        if (editor.value !== text) editor.value = text;
        syncCounter(source);
        resizeField(editor);
      },
    });
    source.after(editor);
    source.value = editor.value;
    source.addEventListener('focus', () => editor.focus());
    let composing = false;
    editor.addEventListener('compositionstart', () => { composing = true; });
    editor.addEventListener('input', (event) => {
      if (event.isComposing) return;
      source.value = editor.value;
      source.dispatchEvent(new Event('input', { bubbles: true }));
    });
    ['keyup', 'change', 'focusout'].forEach((type) => editor.addEventListener(type, () => {
      if (composing) return;
      source.value = editor.value;
      source.dispatchEvent(new Event(type, { bubbles: true }));
    }));
    editor.addEventListener('compositionend', () => {
      composing = false;
      source.value = editor.value;
      source.dispatchEvent(new Event('input', { bubbles: true }));
    });
    sizeObserver.observe(editor);
  };
  const oneDecimal = (text) => text.replace(/\d+(?:[.,]\d+)?/, (value) => Number(value.replace(',', '.')).toFixed(1));
  const syncCounter = (input) => {
    if (!(input instanceof Element) || !input.matches(fieldSelector)) return;
    input = titleSources.get(input) || input;
    const remaining = Math.max(0, (input.name === 'review_title' ? 100 : 5000) - input.value.length);
    input.parentElement.querySelectorAll(':scope > .jdgm-countdown').forEach((counter) => {
      counter.toggleAttribute('data-review-empty', input.value.length === 0);
      const text = `(${remaining})`;
      if (counter.textContent !== text) counter.textContent = text;
    });
  };
  const sync = () => {
    scheduled = false;
    document.querySelectorAll('.jdgm-form').forEach(configureValidation);
    document.querySelectorAll('.jdgm-form :is(input, textarea, select)').forEach((field) => {
      if (!fieldValues.has(field)) fieldValues.set(field, fieldValue(field));
    });
    document.querySelectorAll('.jdgm-form input[name="review_title"]').forEach(enhanceTitle);
    document.querySelectorAll('.jdgm-form textarea[name="review_body"]').forEach((field) => {
      field.maxLength = 5000;
      field.rows = 1;
      if (field.value.length > 5000) field.value = limitValue(field.value, 5000);
      if (!sizes.has(field)) {
        sizes.set(field, {});
        sizeObserver.observe(field);
      }
      enhanceBodyResize(field);
    });
    document.querySelectorAll(fieldSelector).forEach((field) => {
      const editor = titleEditors.get(field) || field;
      if (titleEditors.has(field)) {
        const text = limitValue(field.value, 100);
        if (field.value !== text) field.value = text;
        if (editor.value !== text) editor.value = text;
      }
      resizeField(editor);
      syncCounter(field);
    });
    document.querySelectorAll('.jdgm-form input[name="reviewer_name"]').forEach((input) => {
      if (input.maxLength !== 30) input.maxLength = 30;
    });
    const mediaSettings = window.jdgmSettings || window.jdgm?.originalSettings;
    if (typeof mediaSettings?.enable_review_videos === 'boolean' && mediaSettings.enable_review_pictures !== false) {
      const caption = mediaSettings.enable_review_videos ? 'Photos/Videos (optional)' : 'Photos (optional)';
      document.querySelectorAll('.jdgm-form label').forEach((label) => {
        const textNodes = document.createTreeWalker(label, NodeFilter.SHOW_TEXT);
        let node;
        while ((node = textNodes.nextNode())) {
          if (!/^(?:Picture\/Video|Photos(?:\/Videos)?) \(optional\)$/.test(node.textContent.trim())) continue;
          if (node.textContent.trim() !== caption) node.textContent = node.textContent.replace(node.textContent.trim(), caption);
        }
      });
    }
    document.querySelectorAll('.jdgm-rev-widg__summary-average').forEach((element) => {
      const formatted = oneDecimal(element.textContent);
      if (formatted !== element.textContent) element.textContent = formatted;
    });
    document.querySelectorAll('.jdgm-rev-widg__summary-stars-img[aria-label]').forEach((element) => {
      const original = element.getAttribute('aria-label');
      const formatted = oneDecimal(original);
      if (formatted !== original) element.setAttribute('aria-label', formatted);
    });
  };
  new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(sync);
  }).observe(document.body, { childList: true, subtree: true, characterData: true });
  document.addEventListener('input', (event) => {
    const field = event.target;
    if (!(field instanceof Element) || !field.matches(fieldSelector)) return;
    if (!event.isComposing && field.name === 'review_body' && field.value.length > 5000) field.value = limitValue(field.value, 5000);
    if (field.name === 'review_body' && !field.value && !event.isComposing) {
      const state = sizes.get(field);
      if (state) delete state.manualHeight;
    }
    resizeField(titleEditors.get(field) || field);
    syncCounter(field);
  });
  document.addEventListener('change', (event) => syncCounter(event.target));
  document.addEventListener('input', clearEditedError, true);
  document.addEventListener('change', clearEditedError, true);
  document.addEventListener('compositionend', (event) => {
    const field = event.target;
    if (!field.matches?.('.jdgm-form textarea[name="review_body"]')) return;
    if (field.value.length > 5000) field.value = limitValue(field.value, 5000);
    resizeField(field);
    syncCounter(field);
  });
  document.addEventListener('reset', (event) => {
    if (event.target.matches('.jdgm-form')) {
      event.target.removeAttribute('data-review-submit-attempted');
      requestAnimationFrame(() => {
        event.target.querySelectorAll('textarea').forEach((field) => { sizes.delete(field); });
        sync();
      });
    }
  });
  ['focusin', 'pointerdown'].forEach((type) => document.addEventListener(type, (event) => {
    const form = event.target.closest?.('.jdgm-form');
    if (form) configureValidation(form);
  }, true));
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('.jdgm-submit-rev, .jdgm-form-dynamic__submit-rev, button[type="submit"], input[type="submit"], .jdgm-cancel-rev');
    const form = button?.form || button?.closest('.jdgm-form');
    if (!form?.matches('.jdgm-form') || button.matches(':disabled, [aria-disabled="true"]')) return;
    if (button.matches('.jdgm-cancel-rev')) form.removeAttribute('data-review-submit-attempted');
    else beginSubmission(form);
  }, true);
  document.addEventListener('submit', (event) => {
    if (event.target.matches('.jdgm-form')) beginSubmission(event.target);
  }, true);
  document.addEventListener('pointermove', (event) => {
    if (!event.target.matches?.('.jdgm-form textarea[name="review_body"]')) return;
    const field = event.target;
    const rect = field.getBoundingClientRect();
    field.style.cursor = event.clientX >= rect.right - 18 && event.clientY >= rect.bottom - 18 ? 'ns-resize' : 'text';
  });
  const finishResize = () => {
    if (!resizingBody) return;
    const state = sizes.get(resizingBody);
    if (state) {
      state.dragging = false;
      state.manualHeight = resizingBody.getBoundingClientRect().height;
      state.height = state.manualHeight;
    }
    resizingBody = null;
  };
  document.addEventListener('pointerdown', (event) => {
    finishResize();
    const field = event.target;
    if (event.button !== 0 || !field.matches?.('.jdgm-form textarea[name="review_body"]')) return;
    const rect = field.getBoundingClientRect();
    if (event.clientX < rect.right - 18 || event.clientY < rect.bottom - 18) return;
    const state = sizes.get(field) || {};
    state.dragging = true;
    state.manualHeight = rect.height;
    sizes.set(field, state);
    resizingBody = field;
  }, true);
  window.addEventListener('pointerup', finishResize, true);
  window.addEventListener('mouseup', finishResize, true);
  window.addEventListener('blur', finishResize);
  window.addEventListener('resize', () => document.querySelectorAll('.jdgm-form textarea:is([data-review-title-editor], [name="review_body"])').forEach((field) => { resizeField(field); positionBodyHandle(field); }));
  sync();
})();
